import { useMemo } from 'react'
import { NotFound } from 'components/NotFound'
import { useIsDexcomFlow } from 'hooks/useIsDexcomFlow'
import {
  captureReferredCoachIdFromCurrentUrl,
  REFERRED_BY_COACH_ID_KEY
} from 'hooks/useReferredByCoach'
import { getVideoCallsFlowCustomizationParams } from 'hooks/useVideoCallsFlowCustomization'
import { LocationPaths, SearchParams } from 'location.types'
import { createBrowserRouter, Navigate } from 'react-router'
import {
  FREE_CALL_TRIAL_TEST_GROUP,
  MFP_CREDIT_GLOBAL_POOL_AVAILABLE_KEY,
  PARTNER_ID_KEY
} from 'screens/VideoCalls/utils/constants'
import { isPublicReferralPartner } from 'screens/VideoCalls/utils/referralPartner'
import { Feature, useFeatureFlag } from 'utils/feature'
import { isKeggWebviewFlow } from 'utils/keggWebviewFlow'
import {
  safeGetLocalStorageItem,
  safeSetLocalStorageItem,
  TEST_GROUP_KEY,
  useLocalStorage
} from 'utils/localStorage'
import { UserReferralPartner } from 'types'
import { App } from './App'
import {
  auth,
  onboarding,
  legacyOwnSensorRedirect,
  partnerRoutes,
  uhc,
  root,
  buildVideoCallsFlowRoutes,
  buildPaidVideoCallsFlowRoutes,
  privateVideoCallsRoutes,
  signInRoute as signInVideoCallsRoute,
  ownSensorV2,
  stressManagement
} from './routes'

// Pre-capture the partner from the URL before the first render so the router is built
// with the correct flow routes from the start. Without this, navigating to
// /video-calls/?partner=mfp_credit on first visit causes a brief 404 because the
// router is created with the default flow (partner not yet in localStorage), then
// step navigation reads the updated localStorage and tries to go to a route that
// doesn't exist in the current router.
const capturePartnerFromUrl = () => {
  if (typeof window === 'undefined') {
    return
  }

  const urlPartner = new URLSearchParams(window.location.search)
    .get(SearchParams.Partner)
    ?.toLowerCase()

  if (!urlPartner || safeGetLocalStorageItem(PARTNER_ID_KEY)) {
    return
  }

  if (isPublicReferralPartner(urlPartner)) {
    safeSetLocalStorageItem(PARTNER_ID_KEY, urlPartner)
  }
}

capturePartnerFromUrl()

// Pre-capture the referred coach from the URL before the first render for the
// same reason as partner capture: referral routes are chosen from localStorage
// while the router is being built. Internal schedule-call redirects use a
// separate selectedCoachId parameter, so only external coachId links are captured.
captureReferredCoachIdFromCurrentUrl()

export const useAppRouter = () => {
  const [partner] = useLocalStorage(PARTNER_ID_KEY)
  const [referredCoachId] = useLocalStorage(REFERRED_BY_COACH_ID_KEY)
  const [testGroup] = useLocalStorage(TEST_GROUP_KEY)
  const [mfpCreditGlobalPoolAvailable] = useLocalStorage(MFP_CREDIT_GLOBAL_POOL_AVAILABLE_KEY)

  const isInfluencerFlow = partner === UserReferralPartner.Influencer
  const isKeggFlow = isKeggWebviewFlow(
    partner,
    getVideoCallsFlowCustomizationParams().forceKeggFlow
  )
  const isReferralFlow = Boolean(referredCoachId)
  const isFreeCallTrialFlow = testGroup === FREE_CALL_TRIAL_TEST_GROUP
  const isDexcomFlow = useIsDexcomFlow()
  const dexcomFlowSchedulerAtBeginningEnabled = useFeatureFlag(Feature.DexcomSchedulerAtBeginning)

  const router = useMemo(() => {
    return createBrowserRouter([
      {
        path: '/',
        element: <App />,
        children: [
          {
            index: true,
            element: <Navigate to={LocationPaths.DownloadApp} replace />
          },
          ...auth,
          onboarding,
          partnerRoutes,
          {
            path: LocationPaths.KeggNotEligible,
            lazy: async () => ({
              Component: (await import('screens/VideoCalls/Kegg/NotEligible')).KeggNotEligible
            })
          },
          buildVideoCallsFlowRoutes({ isKeggFlow }),
          buildPaidVideoCallsFlowRoutes({ isKeggFlow }),
          privateVideoCallsRoutes,
          signInVideoCallsRoute,
          stressManagement,
          legacyOwnSensorRedirect,
          ownSensorV2,
          uhc,
          root,
          {
            path: '*',
            element: <NotFound />
          }
        ]
      }
    ])
    // video call routes depend on these variables, so we need to re-create the router when they change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInfluencerFlow,
    isKeggFlow,
    isReferralFlow,
    isFreeCallTrialFlow,
    isDexcomFlow,
    dexcomFlowSchedulerAtBeginningEnabled,
    mfpCreditGlobalPoolAvailable
  ])

  return router
}
